/**
 * Client-side login handoff (AIR-819 root cause fix).
 *
 * `/api/login` is handled inside the auth middleware by next-firebase-auth-edge
 * (`setAuthCookies` -> `getCustomIdAndRefreshTokens`), which verifies the
 * Bearer ID token against Google's public keys and exchanges it with the
 * identity toolkit — network round-trips that can transiently fail, most often
 * on a cold instance whose key cache is empty. The library has no error
 * handling on that path, so a transient failure surfaces as a 500 with NO
 * Set-Cookie. The old client ignored the response entirely, then
 * router.refresh()'d and waited forever for a server-rendered user that could
 * never arrive (the cookie was never set) — the "Signing you in..." hang.
 *
 * This module makes the cookie sync verified and bounded: the response status
 * is checked, transient failures (5xx / network / per-attempt timeout) are
 * retried on a short backoff, and persistent failure throws a typed error the
 * UI can surface instead of spinning.
 */

/** Delays between retry attempts. Two retries: total 3 attempts. */
export const LOGIN_SYNC_RETRY_DELAYS_MS: readonly number[] = [500, 1500];

/**
 * Per-attempt timeout. The middleware's upstream Google calls carry no
 * timeout of their own, so without this a hung attempt would recreate the
 * infinite spinner this fix removes.
 */
export const LOGIN_SYNC_ATTEMPT_TIMEOUT_MS = 8_000;

export class LoginSyncError extends Error {
  /** HTTP status of the final failed attempt, or null for network failures. */
  readonly status: number | null;
  readonly attempts: number;

  constructor(message: string, status: number | null, attempts: number) {
    super(message);
    this.name = "LoginSyncError";
    this.status = status;
    this.attempts = attempts;
  }
}

export interface SyncSessionCookieOptions {
  fetchImpl?: typeof fetch;
  retryDelaysMs?: readonly number[];
  attemptTimeoutMs?: number;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Sets the httpOnly `__session` cookie by calling `/api/login` with the
 * Firebase ID token, and does not resolve until the server has confirmed it
 * (2xx). Retries transient failures; throws LoginSyncError when the cookie
 * could not be established. Callers must not treat the session as usable
 * unless this resolves.
 */
export async function syncSessionCookie(
  idToken: string,
  options: SyncSessionCookieOptions = {},
): Promise<void> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const retryDelaysMs = options.retryDelaysMs ?? LOGIN_SYNC_RETRY_DELAYS_MS;
  const attemptTimeoutMs = options.attemptTimeoutMs ?? LOGIN_SYNC_ATTEMPT_TIMEOUT_MS;
  const maxAttempts = retryDelaysMs.length + 1;

  let lastStatus: number | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetchImpl("/api/login", {
        method: "GET",
        headers: { Authorization: `Bearer ${idToken}` },
        credentials: "include",
        signal: AbortSignal.timeout(attemptTimeoutMs),
      });

      if (response.ok) {
        return;
      }

      lastStatus = response.status;
      // 4xx is definitive (bad/missing token, entitlement block) — retrying
      // the same request cannot succeed.
      if (response.status < 500) {
        throw new LoginSyncError(
          `Login cookie sync rejected with status ${response.status}`,
          response.status,
          attempt,
        );
      }
    } catch (error) {
      if (error instanceof LoginSyncError) {
        throw error;
      }
      // Network failure or per-attempt timeout — retryable.
      lastStatus = null;
    }

    if (attempt < maxAttempts) {
      await sleep(retryDelaysMs[attempt - 1] ?? 0);
    }
  }

  throw new LoginSyncError(
    `Login cookie sync failed after ${maxAttempts} attempts` +
      (lastStatus !== null ? ` (last status ${lastStatus})` : ""),
    lastStatus,
    maxAttempts,
  );
}

/**
 * True when the given pathname is the login page. On the login surface the
 * post-sync handoff is a full navigation (the middleware decides the
 * destination server-side); elsewhere a router.refresh() re-renders the page
 * in place with the new session.
 */
export function isLoginPath(pathname: string): boolean {
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return normalized === "/login";
}
